import { useTranslation } from 'libs/i18n'
import { Select } from 'shared/components'
import { GB, IS, SE, DK, ES } from 'country-flag-icons/react/3x2'
import { OptionProps, Props, SingleValueProps, components } from 'react-select'

import { cn } from 'utils/style'

type LanguageSwitcherProps = {
	variant?: 'default' | 'sidebar' | 'compact'
}

const sidebarSelectProps: Pick<Props, 'unstyled' | 'classNames' | 'menuPlacement'> = {
	unstyled: true,
	menuPlacement: 'top',
	classNames: {
		control: ({ menuIsOpen }) =>
			cn(
				'cursor-pointer rounded-md px-3 py-2 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
				menuIsOpen && 'bg-muted text-foreground'
			),
		dropdownIndicator: () => '[&>svg]:h-3.5 [&>svg]:w-3.5',
		menu: () =>
			'z-[10000] mb-1 overflow-hidden rounded-md border border-border bg-card shadow-panel'
	}
}

const compactSelectProps: Pick<Props, 'unstyled' | 'classNames' | 'menuPlacement'> = {
	...sidebarSelectProps,
	menuPlacement: 'bottom',
	classNames: {
		...sidebarSelectProps.classNames,
		menu: () =>
			'right-0 z-[10000] mt-1 min-w-[10rem] overflow-hidden rounded-md border border-border bg-card shadow-panel'
	}
}

const variantSelectProps = {
	default: {},
	sidebar: sidebarSelectProps,
	compact: compactSelectProps
}

const flags = {
	is: <IS className="h-6 w-6" />,
	en: <GB className="h-6 w-6" />,
	se: <SE className="h-6 w-6" />,
	dk: <DK className="h-6 w-6" />,
	es: <ES className="h-6 w-6" />
}

const CustomOption = ({ children, innerRef, innerProps, isSelected, data }: OptionProps<any>) => {
	return (
		<div
			ref={innerRef}
			{...innerProps}
			className={cn(
				'flex w-full cursor-pointer select-none items-center gap-2 whitespace-nowrap px-3 py-2 text-[length:inherit] hover:bg-dodgerBlue hover:text-white',
				isSelected ? 'bg-dodgerBlue text-white' : 'bg-white text-doveGray'
			)}>
			{data?.value ? flags[data.value as keyof typeof flags] : null}
			{children}
		</div>
	)
}

export const LanguageSwitcher = ({ variant = 'default' }: LanguageSwitcherProps) => {
	const { i18n, t } = useTranslation()
	const isSidebar = variant === 'sidebar'
	const isPlain = variant !== 'default'
	const changeLanguage = (lng: string) => {
		i18n.changeLanguage(lng)
	}

	const languages = [
		{
			label: t('Icelandic'),
			code: 'is'
		},
		{
			label: t('English'),
			code: 'en'
		},
		{
			label: t('Danish'),
			code: 'dk'
		},
		{
			label: t('Swedish'),
			code: 'se'
		},
		{
			label: t('Spanish'),
			code: 'es'
		}
	]

	const CustomSingleValue = ({ ...props }: SingleValueProps<any>) => (
		<components.SingleValue
			{...props}
			className={cn('flex items-center gap-2', isPlain && 'gap-3 [&>svg]:h-4 [&>svg]:w-4')}>
			{props.data?.value ? flags[i18n.language as keyof typeof flags] : null}
			{languages.find((language) => language.code === i18n.language)?.label}
		</components.SingleValue>
	)

	const handleLanguageChange = (e: { value: string; label: string }) => {
		changeLanguage(e.value)
	}

	return (
		<div className="flex gap-3">
			<Select
				id="language"
				isClearable={false}
				defaultValue={{
					value: languages.find((language) => language.code === i18n.language)?.code,
					label: languages.find((language) => language.code === i18n.language)?.label
				}}
				options={languages.map((language) => ({
					value: language.code,
					label: language.label
				}))}
				onChange={handleLanguageChange}
				components={{
					Option: CustomOption,
					IndicatorSeparator: () => null,
					SingleValue: CustomSingleValue
				}}
				className={cn('w-full', isSidebar && 'mt-1 min-w-0')}
				{...variantSelectProps[variant]}
			/>
		</div>
	)
}
